import {
    configure,
    computed,
    observable,
    when,
    makeObservable,
    reaction,
} from 'mobx';
import React, {createContext} from 'react';
import ReactDOM from 'react-dom';
import {toJS} from 'mobx';

import http from 'http.js';
import {MOBILE_MAX_SCREEN_SIZE, TABLET_MAX_SCREEN_SIZE} from 'base/search.js';
import autobind from 'common/decorators/autobind.js';
import ContactModal from 'core/components/ContactModal.js';
import ReferralModal from 'core/components/ReferralModal.js';
import ImageGalleryModal from 'core/components/ImageGalleryModal.js';
import Modal from 'core/components/Modal.js';
import Store from 'core/stores/Store.js';
import {snakeToCamelObjectKeys} from 'utils/case_converter.js';
import {convertToDayNames, toggleLoader} from 'core/utils.js';
import {ConcatenatedTextV2, LinkV2} from '@HealthShareAU/hs-component-library';

// Throws if observable is changed outside of action
configure({enforceActions: 'observed'});

export default class PracticeStoreV2 extends Store {
    constructor(rootStore) {
        super();
        this.rootStore = rootStore;

        makeObservable(this, {
            activeTab: observable,
            customModalProps: observable,
            displayedModal: observable,
            gapSchemeMembership: observable,
            url: observable,
            isPracticeGroup: observable,
            practice: observable,
            practiceFAQs: observable,
            practiceRelatedContent: observable,
            practiceLoaded: observable,
            practiceRelatdContentLoaded: observable,

            displayTabsOnPage: computed,
            tabs: computed,

            // For redesign
            bookingsTab: computed,
            chips: computed,
            collapseSections: computed,
            practiceTabs: computed,
            pills: computed,

            // This is necessary for skinned view where the practice has practitioners
            // but none of them have active gap scheme participation
            hasPractitioners: computed,
            gapSchemePractitioners: computed,
            nonGapSchemePractitioners: computed,
        });

        when(
            () => this.url,
            () => this.fetchPracticeInfo(),
        );
        when(
            () => this.url,
            () => this.fetchPracticeRelatedContent(),
        );
        when(
            () => this.url,
            () => this.fetchPracticeFAQs(),
        );
        when(
            () => this.practiceLoaded,
            () => this.trackPageOpen(),
        );

        reaction(
            () =>
                !this.isHospitalPage &&
                this.practiceLoaded &&
                this.rootStore?.healthFundStore.healthFund?.id,
            (healthFundId) => {
                this.fetchGapSchemeMembership(healthFundId);
            },
        );

        reaction(
            () => this.displayTabsOnPage && this.practiceLoaded,
            () => {
                this.setActiveTab();
                this.fetchActiveTab();
            },
        );
        reaction(
            () => this.isMobileOrTablet,
            () => this.setActiveTab(),
        );
        reaction(
            () => this.practiceLoaded,
            (loaded) => {
                toggleLoader(loaded);
            },
        );
    }

    // New tabs for redesign
    locationTab = {
        title: 'Location',
        'hash': 'location',
        'component': 'LocationSection',
    };

    galleriesTab = {
        title: 'Galleries',
        'hash': 'galleries',
        component: 'GalleriesSection',
    };

    aboutTab = {
        title: 'About',
        hash: 'about',
        component: 'AboutSection',
    };

    relatedInfoTab = {
        title: 'Related info',
        hash: 'related-info',
        component: 'PracticeRelatedInfoV2',
    };
    faqTab = {
        title: 'FAQ',
        hash: 'faq',
        component: 'PracticeFAQ',
    };

    activeTab = null;
    client = undefined;
    customModalProps = {};
    displayedModal = undefined;
    gapSchemeMembership = [];

    url = null;
    isPracticeGroup = false;
    practice = {};
    practiceFAQs = [];
    practiceRelatedContent = {};
    practiceLoaded = false;
    practiceRelatdContentLoaded = false;
    url = null;

    displayToolsOnPage = true;

    @autobind
    closeModal() {
        this.updateStore({
            customModalProps: {},
            displayedModal: null,
        });
    }

    @autobind
    showModal(modalType, modalProps) {
        this.updateStore({
            customModalProps: modalProps,
            displayedModal: modalType,
        });
    }

    @autobind
    async fetchGapSchemeMembership(healthFundId) {
        let data =
            (await http.post({
                url: `/api/professionals/v1/gap-scheme-membership/health-fund/${healthFundId}`,
                data: {userprofile_ids: this.practitionerIds},
            })) || [];
        data = data.map((d) => snakeToCamelObjectKeys(d));
        this.updateStore({gapSchemeMembership: data});
    }

    get practitionerIds() {
        return this.practice.practitioners?.map((pr) => pr.userprofileId);
    }

    get displayTabsOnPage() {
        return true;
    }

    get bookingsTab() {
        const {isReferrals, isProUser} = this.rootStore.paramStore;
        return {
            title:
                isReferrals || isProUser
                    ? 'Practitioners'
                    : 'Book an appointment',
            hash: 'book-an-appointment',
            component: 'BookingSection',
        };
    }

    get practiceTabs() {
        const displayTabs = [];
        if (this.practiceLoaded) {
            const practice = toJS(this.practice);

            let isShowGalleriesTab = false;

            const {description, images, videos} = practice;

            if (!this.rootStore.paramStore.isClient) {
                if (images?.length > 0 || videos?.length > 0) {
                    isShowGalleriesTab = true;
                }
            }

            // booking section pass
            if (this.hasPractitioners) {
                displayTabs.push(this.bookingsTab);
            }

            // About section pass
            if (description) {
                displayTabs.push(this.aboutTab);
            }

            // location pass
            displayTabs.push(this.locationTab);

            // galleries
            if (isShowGalleriesTab) {
                displayTabs.push(this.galleriesTab);
            }

            // FAQ
            if (this.practiceFAQs.length) {
                displayTabs.push(this.faqTab);
            }

            // related info
            if (
                (!this.rootStore.paramStore.isClient &&
                    !this.practice.enhanced &&
                    !this.practice.premiumPartner &&
                    this.practiceRelatedContent.relatedFactsheets?.length >
                        0) || //ignoring communities for now
                this.practice.files.length > 0
            ) {
                displayTabs.push(this.relatedInfoTab);
            }
        }
        return displayTabs;
    }

    get collapseSections() {
        const {
            openIndicator,
            extraInfo: {openHours, paymentOptions, serviceOptions, comment},
        } = this.practice;

        const sections = [];

        if (openHours.length) {
            openHours.map(
                (daySched) =>
                    (daySched.day =
                        convertToDayNames[daySched.day.toLowerCase()]),
            );
            sections.push({
                header: `Opening Hours${
                    openIndicator ? ` (${openIndicator})` : ''
                }`.trim(),
                contents: [...openHours],
                extraInfo: comment,
            });
        }

        if (paymentOptions.length) {
            sections.push({
                header: 'Payment Options',
                contents: [...paymentOptions],
            });
        }

        if (serviceOptions) {
            sections.push({
                header: 'Service Options',
                contents: [...serviceOptions.split(',')],
            });
        }

        return sections;
    }

    get pills() {
        const pills = [];
        const isDisabledAccess = this.practice?.disabledAccess;
        const isOnsiteParking = this.practice?.onsiteParking;

        if (isDisabledAccess) {
            pills.push({
                text: 'Disabled access',
                iconFront: 'wheelchair',
            });
        }

        if (isOnsiteParking) {
            pills.push({
                text: 'On-site parking',
                iconFront: 'parking',
            });
        }
        return pills;
    }

    get tabs() {
        const displayTabs = [];
        if (this.practiceLoaded) {
            if (
                !this.rootStore.paramStore.isClient &&
                !this.practice.enhanced &&
                !this.practice.premiumPartner &&
                Object.keys(this.practiceRelatedContent).length > 0
            ) {
                displayTabs.push(this.relatedInfoTab);
            }
            if (this.practiceFAQs.length) {
                displayTabs.push(this.faqTab);
            }
            if (
                this.practice.locations?.length &&
                this.isPracticeGroup &&
                !this.rootStore.paramStore.isClient
            ) {
                const locationsTab = {
                    title: 'Locations',
                    hash: 'locations',
                    component: 'LocationList',
                    count: this.practice.locations.length,
                };
                const firstTab = displayTabs.shift();
                displayTabs.unshift(firstTab, locationsTab);
            }
        }
        return displayTabs;
    }

    get chips() {
        const {
            paramStore: {hasSkin},
            healthFundStore: {healthFundThemeColor},
        } = this.rootStore;

        const practice = this.practice;
        const colorTheme =
            hasSkin && healthFundThemeColor ? healthFundThemeColor : null;
        const externalUrl = practice?.website;
        const phones = practice?.phones;
        const fax = practice?.fax;
        const practiceLocationAddress = `${practice?.streetAddress.trim()}${
            practice?.streetAddress.trim()
                ? practice?.locality && ', ' + practice?.locality
                : practice?.locality
        }`;

        const chip = [];
        if (practiceLocationAddress) {
            let googlePreviewUrl;
            if (practice.streetAddress && practice.locality) {
                googlePreviewUrl = `https://www.google.com/maps/preview/?q=${practice.streetAddress}${practice.locality}`;
            }
            chip.push({
                icon: 'markerPin',
                text: practiceLocationAddress,
                children: (
                    <LinkV2
                        colorTheme={colorTheme}
                        onClick={() => {}}
                        text={practiceLocationAddress}
                        href={googlePreviewUrl}
                    />
                ),
            });
        }
        if (phones?.length) {
            phones?.map((phone) => {
                chip.push({
                    text: phone?.number,
                    customClass: 'phone',
                    icon: 'phone',
                    key: 'phone-1',
                    extraInfo: phone?.description,
                    children: (
                        <ConcatenatedTextV2
                            colorTheme={colorTheme}
                            customClass="no-underline"
                            displayLink={`tel: ${phone?.number}`}
                            expandText="Show phone number"
                            length={8}
                            text={phone?.number}
                        />
                    ),
                });
            });
        }
        if (fax?.id) {
            chip.push({
                icon: 'printer',
                text: fax?.number,
                extraInfo: fax?.description,
                children: (
                    <ConcatenatedTextV2
                        colorTheme={colorTheme}
                        customClass="no-underline"
                        displayLink={`tel: ${fax?.number}`}
                        expandText="Show fax number"
                        length={8}
                        text={fax?.number}
                    />
                ),
            });
        }
        if (externalUrl) {
            chip.push({
                icon: 'share',
                children: <LinkV2 text="Visit website" href={externalUrl} />,
            });
        }

        return chip;
    }

    get gapSchemePractitioners() {
        const {
            gapSchemeMembership,
            practice: {practitioners},
        } = this;
        const gapSchemes = gapSchemeMembership
            .filter((pr) => pr.hasGapScheme === true)
            .map((pr) => pr.id);
        return practitioners.filter((pr) =>
            gapSchemes.includes(pr.userprofileId),
        );
    }

    get nonGapSchemePractitioners() {
        const {
            gapSchemeMembership,
            practice: {practitioners},
        } = this;
        const nonGapSchemes = gapSchemeMembership
            .filter((pr) => pr.hasGapScheme === false)
            .map((pr) => pr.id);
        return practitioners.filter((pr) =>
            nonGapSchemes.includes(pr.userprofileId),
        );
    }

    get hasPractitioners() {
        const {
            practice: {practitioners},
            rootStore: {
                healthFundStore: {healthFund},
            },
        } = this;

        return healthFund?.id
            ? !!this.gapSchemePractitioners?.length ||
                  !!this.nonGapSchemePractitioners?.length
            : !!practitioners?.length;
    }

    @autobind
    async selectHealthFundHandler(hf) {
        await this.rootStore.healthFundStore.selectHealthFund(hf?.id || 0);

        if (this?.isHospitalPage) {
            const hospitalId = parseInt(
                window.sessionStorage.getItem('hospitalId'),
            );
            if (this.practice.id !== hospitalId) {
                window.sessionStorage.setItem('hospitalId', this.practice.id);
            }
            window.location.reload();
        }
    }

    @autobind
    renderModal() {
        if (!this.practiceLoaded) {
            return null;
        }
        let ModalContent;
        let qs = document.querySelector('#practice-group');
        if (!qs) {
            qs = document.querySelector('#practice-location');
        }
        if (!qs) {
            qs = document.querySelector('#hospital-page');
        }
        switch (this.displayedModal) {
            case 'hospitalContact':
                ModalContent = (
                    <ContactModal
                        {...this.customModalProps}
                        closeModal={this.closeModal}
                        isHospitalDirectory={true}
                    />
                );

                break;
            case 'contact':
                ModalContent = (
                    <ContactModal
                        {...this.contactProps}
                        analytics={this.analytics}
                        closeModal={this.closeModal}
                        {...this.customModalProps}
                    />
                );
                break;
            case 'gallery':
                ModalContent = (
                    <ImageGalleryModal
                        closeModal={this.closeModal}
                        images={this.practice.images}
                        {...this.customModalProps}
                    />
                );
                break;
            case 'referralModal':
                ModalContent = (
                    <ReferralModal
                        closeModal={this.closeModal}
                        {...this.customModalProps}
                    />
                );
                break;
            default:
                throw new Error('Cannot render an invalid modal type');
        }
        return ReactDOM.createPortal(
            <Modal
                closeModal={this.closeModal}
                initElement={document.activeElement}
                parentElement={document.activeElement.parentElement}
            >
                {ModalContent}
            </Modal>,
            qs,
        );
    }

    @autobind
    setActiveTab() {
        const activeTab = this.fetchDefaultTab();
        const fallbackHash = activeTab?.hash.length
            ? `#${activeTab.hash}`
            : '';
        this.updateStore({activeTab: activeTab});
        this.replaceHashWith(fallbackHash);
    }

    @autobind
    fetchDefaultTab() {
        let activeTab = null;
        activeTab = this.tabs[0];
        return activeTab;
    }

    @autobind
    fetchActiveTab() {
        const tabHash = window.location.hash.split('#')[1];
        const activeTab = this.tabs.find((tab) => tab.hash === tabHash);
        if (tabHash && activeTab) {
            this.updateStore({activeTab});
            this.replaceHashWith(`#${activeTab.hash}`);
        } else {
            this.setActiveTab();
        }
    }

    @autobind
    onWindowResize() {
        const isMobileOrTablet = window.matchMedia(
            `(max-width: ${TABLET_MAX_SCREEN_SIZE})`,
        ).matches;
        this.updateStore({isMobileOrTablet});
        if (!isMobileOrTablet && this.activeTab.hash === 'locations') {
            this.setActiveTab();
        }
    }

    replaceHashWith(value) {
        if (value) {
            history.replaceState('', '', value);
        } else {
            history.replaceState(
                '',
                document.title,
                location.pathname + location.search,
            );
        }
    }

    @autobind
    async fetchPracticeInfo() {
        let practice = {};
        try {
            const response = await http.get({
                url: this.url,
                data: {
                    show: this.rootStore.paramStore.showParam,
                },
            });
            practice = snakeToCamelObjectKeys(response);
        } catch (error) {
            this.throwFetchPracticeError(error);
        } finally {
            this.updateStore({practice, practiceLoaded: true});
        }
    }

    @autobind
    async fetchPracticeRelatedContent() {
        let practiceRelatedContent = {};
        let response;
        try {
            response = await http.get({
                url: `${this.url}related-content/`,
            });
        } catch (error) {
            this.throwRelatedContentError(error);
        } finally {
            // replacement to .flat() which doesn't work on older browsers.
            if (response) {
                // for redesign we only need factsheets
                const {related_topics, ...factsheets} = response;
                const data = Object.values(factsheets);
                if ([].concat(...Object.values(data)).length) {
                    practiceRelatedContent = snakeToCamelObjectKeys(
                        factsheets,
                    );
                }
            }
            this.updateStore({
                practiceRelatedContent,
                practiceRelatedContentLoaded: true,
            });
        }
    }

    @autobind
    async fetchPracticeFAQs() {
        let practiceFAQs = [];
        try {
            const responseList = await http.get({
                url: `${this.url}faqs/`,
            });
            practiceFAQs = responseList.map((res) =>
                snakeToCamelObjectKeys(res),
            );
        } catch (error) {
            this.throwFAQError(error);
        } finally {
            this.updateStore({practiceFAQs});
        }
    }

    @autobind
    throwRelatedContentError(error) {
        throw new Error(`Fetching practice related content failed ${error}`);
    }

    @autobind
    throwFAQError(error) {
        throw new Error(`Fetching practice FAQs failed ${error}`);
    }

    @autobind
    throwFetchPracticeError(error) {
        throw new Error(`Fetching practice failed ${error}`);
    }
}
